import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getOrderDetails, updateOrderStatus } from '../../services/supabaseService';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { supabase } from '../../lib/supabase';

export const LojistaOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchOrder = async () => {
    if (!id) return;
    try {
      const data = await getOrderDetails(id);
      if (data) {
        // Hydrate buyer username if missing
        if (data.buyer_id && (!data.buyer || !data.buyer.username)) {
          try {
            const numericBuyerId = Number(data.buyer_id);
            const { data: buyerData } = await (supabase.rpc('get_safe_users') as any)
              .select('id, username, fullname, avatar')
              .eq('id', numericBuyerId)
              .maybeSingle();

            if (buyerData) {
              data.buyer = { ...data.buyer, ...buyerData };
            }
          } catch (rpcErr) {
            console.warn('Silent fallback for safe_users in LojistaOrderDetails:', rpcErr);
          }
        }
        setOrder(data);
      }
    } catch (err) {
      console.error('Error loading order details:', err);
      showNotification('Erro ao carregar detalhes do pedido.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();

    // Supabase Realtime Subscription for live order updates
    let channel: any = null;
    if (id) {
      channel = supabase
        .channel(`lojista_order_details_${id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `id=eq.${id}`,
          },
          async (payload: any) => {
            console.log('[Lojista Realtime] Pedido atualizado:', payload);
            fetchOrder();
          }
        )
        .subscribe();
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [id]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pago', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
      case 'pending':
      case 'pending_payment':
      case 'waiting_payment':
        return { label: 'Aguardando Pagamento', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
      case 'processing':
        return { label: 'Em Preparação', class: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
      case 'shipped':
        return { label: 'Enviado', class: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' };
      case 'delivered':
        return { label: 'Entregue', class: 'bg-teal-500/15 text-teal-400 border-teal-500/30' };
      case 'expired':
        return { label: 'Expirado', class: 'bg-slate-700/40 text-slate-400 border-slate-600/30' };
      case 'canceled':
      case 'cancelled':
        return { label: 'Cancelado', class: 'bg-rose-500/15 text-rose-400 border-rose-500/30' };
      default:
        return { label: status, class: 'bg-slate-800 text-slate-300 border-slate-700' };
    }
  };

  const getAvailableStatusOptions = (currentStatus: string) => {
    const status = currentStatus || 'pending_payment';
    if (status === 'pending_payment' || status === 'waiting_payment' || status === 'pending') {
      return [
        { value: status, label: 'Pendente (Aguard. Pagamento)' },
        { value: 'paid', label: 'Marcar como Pago' },
        { value: 'cancelled', label: 'Cancelar Pedido' }
      ];
    }
    if (status === 'paid') {
      return [
        { value: 'paid', label: 'Pago' },
        { value: 'processing', label: 'Iniciar Preparação' },
        { value: 'shipped', label: 'Marcar como Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    if (status === 'processing') {
      return [
        { value: 'processing', label: 'Em Preparação' },
        { value: 'shipped', label: 'Marcar como Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    if (status === 'shipped') {
      return [
        { value: 'shipped', label: 'Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    return [];
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !order) return;
    if (order.status === newStatus) return;

    const isFinal = ['cancelled', 'canceled', 'expired', 'refunded', 'delivered'].includes(order.status);
    if (isFinal) {
      Swal.fire({
        title: 'Status Final',
        text: 'Este pedido já está finalizado e seu status não pode ser alterado.',
        icon: 'warning',
        confirmButtonColor: '#9333ea',
        background: '#0f172a',
        color: '#fff'
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Alterar Status?',
      text: `Deseja atualizar este pedido para "${newStatus.toUpperCase()}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, atualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#9333ea',
      cancelButtonColor: '#334155',
      background: '#0f172a',
      color: '#fff'
    });

    if (!confirm.isConfirmed) return;

    setUpdatingStatus(true);
    try {
      const res = await updateOrderStatus(id, newStatus);
      if (res?.ok) {
        setOrder((prev: any) => ({ ...prev, status: newStatus }));
        showNotification('Status do pedido atualizado com sucesso!', 'success');
      } else {
        throw new Error(res?.error || 'Erro ao alterar status');
      }
    } catch (err: any) {
      console.error('Error changing order status:', err);
      showNotification(`Falha ao alterar status: ${err.message || 'Erro de conexão'}`, 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center space-y-3">
        <div className="w-8 h-8 border-3 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Carregando detalhes do pedido...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-20 text-center space-y-4">
        <p className="text-sm font-bold text-slate-400">Pedido não encontrado ou você não possui permissão para acessá-lo.</p>
        <Link
          to="/pedidos-recebidos"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold"
        >
          <i className="fas fa-arrow-left"></i> Voltar aos Pedidos
        </Link>
      </div>
    );
  }

  const subtotal = order.products_total ?? order.order_items?.reduce((sum: number, item: any) => sum + (item.unit_price * item.quantity), 0) ?? (order.amount - (order.shipping_price || 0));
  const shippingPrice = order.shipping_price || 0;
  const totalAmount = order.amount || (subtotal + shippingPrice);
  const badge = getStatusBadge(order.status);
  const availableOptions = getAvailableStatusOptions(order.status);
  const refCode = order.external_reference
    ? order.external_reference.slice(-7).toUpperCase()
    : String(order.id).slice(-7).toUpperCase();

  const buyerInitials = order.buyer?.fullname
    ? order.buyer.fullname.slice(0, 2).toUpperCase()
    : order.buyer?.username
    ? order.buyer.username.slice(0, 2).toUpperCase()
    : 'CL';

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Top Bar Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pedidos-recebidos')}
            className="w-9 h-9 bg-slate-900 border border-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-700 transition-colors cursor-pointer"
            title="Voltar aos pedidos"
          >
            <i className="fas fa-arrow-left text-xs"></i>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black text-white uppercase tracking-tight">
                Pedido #{refCode}
              </h1>
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${badge.class}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 font-medium">
              Realizado em {new Date(order.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>

        {/* Quick Status Updater */}
        {availableOptions.length > 0 && (
          <div className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 p-1.5 rounded-xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 ml-2">
              Alterar Status:
            </span>
            <select
              disabled={updatingStatus}
              value={order.status || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg px-3 py-1.5 focus:outline-none focus:border-purple-500 cursor-pointer disabled:opacity-50"
            >
              {availableOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (2 cols): Items & Buyer Details */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items Card */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
                <i className="fas fa-boxes-stacked text-purple-400"></i>
                Itens do Pedido ({order.order_items?.length || 0})
              </h3>
            </div>
            <div className="divide-y divide-slate-800/60 p-2">
              {order.order_items?.map((item: any) => {
                const itemTotal = (item.unit_price || 0) * (item.quantity || 1);
                return (
                  <div key={item.id} className="flex items-center gap-3.5 p-3 hover:bg-slate-800/30 rounded-lg transition-colors">
                    <div className="w-12 h-12 bg-slate-800 rounded-lg overflow-hidden shrink-0 border border-slate-700/60">
                      <img
                        src={item.product?.image_url || 'https://via.placeholder.com/150'}
                        alt={item.product?.name || 'Produto'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-white truncate">
                        {item.product?.name || 'Produto'}
                      </h4>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Qtd: <span className="text-slate-300 font-bold">{item.quantity}</span> × R$ {(item.unit_price || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-black text-white">
                        R$ {itemTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Buyer Information Card */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <h3 className="text-xs font-black uppercase tracking-wider text-white mb-4 flex items-center gap-2">
              <i className="fas fa-user text-purple-400"></i>
              Dados do Comprador
            </h3>
            <div className="flex items-center gap-4">
              {order.buyer?.avatar ? (
                <img
                  src={order.buyer.avatar}
                  alt={order.buyer?.username || 'Cliente'}
                  className="w-12 h-12 rounded-full object-cover border border-slate-700"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-black text-purple-400">
                  {buyerInitials}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white">
                  {order.buyer?.fullname || order.buyer?.username || 'Cliente Convidado'}
                </p>
                {order.buyer?.username && (
                  <Link
                    to={`/profile/${order.buyer.username}`}
                    className="text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium flex items-center gap-1 mt-0.5"
                  >
                    <span>@{order.buyer.username}</span>
                    <i className="fas fa-arrow-up-right-from-square text-[9px]"></i>
                  </Link>
                )}
                {order.buyer?.email && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                    <i className="fas fa-envelope text-[10px]"></i>
                    {order.buyer.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery / Shipping Address if any */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <h3 className="text-xs font-black uppercase tracking-wider text-white mb-3 flex items-center gap-2">
              <i className="fas fa-truck text-purple-400"></i>
              Entrega / Retirada
            </h3>
            {order.shipping_address ? (
              <div className="text-xs text-slate-300 space-y-1">
                <p className="font-bold text-white">{order.shipping_address.recipient_name || 'Destinatário'}</p>
                <p>{order.shipping_address.street}, {order.shipping_address.number} {order.shipping_address.complement || ''}</p>
                <p>{order.shipping_address.neighborhood} - {order.shipping_address.city}/{order.shipping_address.state}</p>
                <p className="text-slate-500">CEP: {order.shipping_address.zip_code}</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <i className="fas fa-store text-emerald-400"></i>
                <span>Retirada no Balcão da Loja ou entrega combinada diretamente</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column (1 col): Status, Payment, Summary */}
        <div className="space-y-5">
          {/* Payment Method & Status Card */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
              <i className="fas fa-circle-info text-purple-400"></i>
              Status & Pagamento
            </h3>

            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Estado Atual
              </span>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border ${badge.class}`}>
                  {badge.label}
                </span>
              </div>
            </div>

            {order.payment_method?.name && (
              <div className="pt-3 border-t border-slate-800">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                  Método de Pagamento
                </span>
                <p className="text-xs font-bold text-white flex items-center gap-1.5">
                  <i className="fas fa-credit-card text-purple-400"></i>
                  {order.payment_method.name}
                </p>
              </div>
            )}

            <div className="pt-3 border-t border-slate-800 text-[11px] text-slate-400 space-y-1">
              <p>
                <span className="text-slate-500">ID do Pedido:</span>{' '}
                <span className="font-mono text-slate-300">#{order.id}</span>
              </p>
              {order.external_reference && (
                <p>
                  <span className="text-slate-500">Ref. Externa:</span>{' '}
                  <span className="font-mono text-slate-300">{order.external_reference}</span>
                </p>
              )}
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-slate-900/60 border border-purple-500/20 bg-purple-950/10 rounded-xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-white">
              Resumo Financeiro
            </h3>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Subtotal dos Itens</span>
                <span className="font-bold text-slate-200">R$ {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Frete</span>
                <span className="font-bold text-slate-200">
                  {shippingPrice > 0 ? `R$ ${shippingPrice.toFixed(2)}` : 'Grátis / Retirada'}
                </span>
              </div>
              <div className="pt-2 border-t border-slate-800 flex justify-between items-baseline">
                <span className="text-xs font-black uppercase tracking-wider text-white">Total</span>
                <span className="text-lg font-black text-purple-400">
                  R$ {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
